import React, { useState, useEffect } from 'react';
import { Call } from 'react-iconly';
import { fetchProjects, fetchTestimonials } from '../services/supabaseService';
import { useTransition } from '../context/TransitionContext';
import { slugify } from '../utils/subdomain';
import { devError } from '../utils/devLog';
import './HomeWorkBento.css';

const HomeWorkBento = () => {
  const { startTransition } = useTransition();
  const [projects, setProjects] = useState([]);
  const [latestTestimonial, setLatestTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        // We need 7 projects for this specific layout
        const [projectsData, testimonialsData] = await Promise.all([
          fetchProjects(),
          fetchTestimonials()
        ]);
        setProjects(projectsData?.slice(0, 7) || []);
        if (testimonialsData && testimonialsData.length > 0) {
          setLatestTestimonial(testimonialsData[0]); // Take the first one which is usually the newest if ordered correctly
        }
      } catch (err) {
        devError('Error loading bento data:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--v2-color-taupe)',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--v2-color-taupe)' }}>
        <p>{error}</p>
      </div>
    );
  }

  const handleProjectClick = (project) => {
    if (project && project.title) {
      startTransition(`/work/${slugify(project.title)}`);
    }
  };

  // Helper to render a project slot safely
  const renderSlot = (project, className) => {
    if (!project) return null; // If we have fewer than 7 projects
    
    return (
      <div 
        key={project.id} 
        className={`bento-item ${className}`}
        onClick={() => handleProjectClick(project)}
      >
        <img 
          src={project.image || `https://via.placeholder.com/800x600/171412/8E827C?text=${encodeURIComponent(project.title)}`} 
          alt={project.title} 
        />
        <div className="bento-content">
          <h3 className="bento-title">{project.title}</h3>
          {project.category && (
            <span className="bento-category">{project.category}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bento-container">
      <div className="bento-grid">
        {/* Row 1: Wide */}
        {renderSlot(projects[0], 'bento-wide')}

        {/* Row 2: Square (Left) + Tall (Right) */}
        {renderSlot(projects[1], 'bento-square')}
        {renderSlot(projects[2], 'bento-tall')}

        {/* Row 3: Testimonial (Left) - sits under the Square from Row 2 */}
        <div className="bento-testimonial">
          <div className="bento-testi-label">Testimonial</div>
          <div className="bento-testi-quote" style={{ fontSize: latestTestimonial?.text?.length > 150 ? 'clamp(18px, 2vw, 24px)' : 'clamp(24px, 3vw, 32px)' }}>
            "{latestTestimonial?.text || "Thank you Brand Appart, I love working with you! Why? Simple: because you're not just a service provider. You don't work for us, you work with us and that makes all the difference."}"
          </div>
          <div className="bento-testi-author-area">
            <div className="bento-testi-author">
              {latestTestimonial?.avatar ? (
                <img src={latestTestimonial.avatar} alt={latestTestimonial?.author} />
              ) : (
                <div style={{width: 48, height: 48, borderRadius: '50%', background: 'white', color: '#3b28cc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '20px'}}>
                  {latestTestimonial?.author?.charAt(0) || "J"}
                </div>
              )}
              <div className="bento-testi-author-info">
                <span className="bento-testi-name">{latestTestimonial?.author || "Jason Benichou"}</span>
                <span className="bento-testi-role">{latestTestimonial?.role || "CTO & Founder @Novo & @BlaBla"}</span>
              </div>
            </div>
            <div className="bento-testi-contact">
              Contact Sales
              <div className="bento-contact-btn">
                <Call size={14} set="bold" />
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Wide */}
        {renderSlot(projects[3], 'bento-wide')}

        {/* Row 5 & 6: Tall (Left) + 2 Squares (Right) */}
        {renderSlot(projects[4], 'bento-tall')}
        {renderSlot(projects[5], 'bento-square')}
        {renderSlot(projects[6], 'bento-square')}
      </div>
    </div>
  );
};

export default HomeWorkBento;
